import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Reason } from "@bitory-gmbh/nidavellir";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Confirmation } from "@/services/backend/vbs/confirmations/types/confirmation";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const vbsConfirmationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsConfirmationCreate: build.mutation<Confirmation, CreateRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationCreateFromOld: build.mutation<
      Confirmation,
      CreateFromOldRequest
    >({
      query: (request) => ({
        url: "/vbs/confirmation/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationCreateFromOffer: build.mutation<
      Confirmation,
      CreateFromOfferRequest
    >({
      query: (request) => ({
        url: "/vbs/confirmation/create-from-offer",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationShow: build.query<Confirmation, ShowRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsConfirmation", id: result.id }] : [],
    }),
    vbsConfirmationList: build.query<Lst<Confirmation>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/vbs/confirmation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsConfirmation"),
    }),
    vbsConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
        "VbsCalculation",
        "VbsTreatment",
        "VbsPricing",
        "VbsPricingByArea",
      ],
    }),
    vbsConfirmationPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
      ],
    }),
    vbsConfirmationPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    vbsConfirmationSetImmutable: build.mutation<
      Confirmation,
      SetImmutableRequest
    >({
      query: (request) => ({
        url: `/vbs/confirmation/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
        "NumbersDocumentNumber",
      ],
    }),
    vbsConfirmationDelete: build.mutation<Confirmation, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
      ],
    }),
  }),
});

export const {
  useVbsConfirmationShowQuery,
  useVbsConfirmationCreateMutation,
  useVbsConfirmationCreateFromOfferMutation,
  useVbsConfirmationCreateFromOldMutation,
  useVbsConfirmationListQuery,
  useVbsConfirmationPatchMutation,
  useVbsConfirmationSetImmutableMutation,
  useVbsConfirmationPdfPreviewQuery,
  useVbsConfirmationPdfDownloadMutation,
  useVbsConfirmationDeleteMutation,
} = vbsConfirmationApi;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOfferRequest {
  offerId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldConfirmationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface DeleteRequest {
  id: string;
}

export type PatchRequest = {
  id: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface PDFRequest {
  id: string;
  document: ConfirmationDocument;
}

export interface SetImmutableRequest {
  id: string;
  reason: Reason;
  setBy: string;
}

export enum ConfirmationImmutabilityReason {
  SetByUser = "set_by_user",
}

export enum ConfirmationDocument {
  All = "all",
  Letter = "letter",
  Treatment = "treatment",
  TreatmentByArea = "treatment-by-area",
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateVbsConfirmationCache = updateCache(
  vbsConfirmationApi,
  "vbsConfirmationShow",
);
